import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/Button";
import { Label } from "@/components/ui/Label";
import { Input } from "@/components/ui/Input";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { Copy, Sparkles, Check, Play, Trash2, Plus, PenTool } from "lucide-react";
import { copyToClipboard } from "@/lib/utils";

interface PathCommand {
  type: "M" | "L" | "C" | "S" | "Q" | "T" | "A" | "Z";
  params: number[];
}

export function SvgPathEditor() {
  const [pathString, setPathString] = useState("M 10 10 L 90 10 C 90 90, 10 90, 10 50 Z");
  const [commands, setCommands] = useState<PathCommand[]>([]);
  const [copied, setCopied] = useState(false);

  // New step fields
  const [newType, setNewType] = useState<PathCommand["type"]>("L");
  const [x, setX] = useState("50");
  const [y, setY] = useState("50");
  const [cx1, setCx1] = useState("30");
  const [cy1, setCy1] = useState("30");
  const [cx2, setCx2] = useState("70");
  const [cy2, setCy2] = useState("70");

  const parsePath = (path: string) => {
    // Basic path parser to parse Move (M), Line (L), Bezier (C), Close (Z)
    const regex = /([MLCSQTAZmlcsqtaz])([^MLCSQTAZmlcsqtaz]*)/g;
    const cmds: PathCommand[] = [];
    let match;

    while ((match = regex.exec(path)) !== null) {
      const type = match[1].toUpperCase() as PathCommand["type"];
      const paramStr = match[2].trim();
      const params = paramStr
        ? paramStr.split(/[\s,]+/).map(p => parseFloat(p)).filter(p => !isNaN(p))
        : [];
      cmds.push({ type, params });
    }
    setCommands(cmds);
  };

  useEffect(() => {
    parsePath(pathString);
  }, [pathString]);

  const updatePathStringFromCommands = (newCmds: PathCommand[]) => {
    const pStr = newCmds.map(cmd => {
      if (cmd.type === "Z") return "Z";
      return `${cmd.type} ${cmd.params.join(" ")}`;
    }).join(" ");
    setPathString(pStr);
  };

  const handleCopy = () => {
    copyToClipboard(pathString).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleClear = () => {
    setPathString("");
    setCommands([]);
  };

  const handleAddCommand = () => {
    const px = parseFloat(x) || 0;
    const py = parseFloat(y) || 0;
    const pcx1 = parseFloat(cx1) || 0;
    const pcy1 = parseFloat(cy1) || 0;
    const pcx2 = parseFloat(cx2) || 0;
    const pcy2 = parseFloat(cy2) || 0;

    let params: number[] = [];
    if (newType === "M" || newType === "L") {
      params = [px, py];
    } else if (newType === "C") {
      params = [pcx1, pcy1, pcx2, pcy2, px, py];
    } else if (newType === "Q") {
      params = [pcx1, pcy1, px, py];
    }

    const newCmd: PathCommand = { type: newType, params };
    const updated = [...commands, newCmd];
    setCommands(updated);
    updatePathStringFromCommands(updated);
  };

  const handleDeleteCommand = (index: number) => {
    const updated = commands.filter((_, i) => i !== index);
    setCommands(updated);
    updatePathStringFromCommands(updated);
  };

  return (
    <ToolPageLayout
      title="SVG Path Editor Online - Draw and Create SVG Coordinates"
      description="Visual SVG path editor and parser. Construct, parse, modify, and build complex SVG path commands (M, L, C, S, Q, T, A, Z) visually on an interactive grid coordinate canvas."
      h1="SVG Path Editor"
      category="SVG Tools"
      categoryPath="/svg-tools"
      howToUse={[
        "Type or paste standard SVG 'd' path parameters inside the text box.",
        "Observe the rendered path shape dynamically in the interactive 100x100 grid canvas below.",
        "Use the quick command builder form to construct and append Move, Line, or Bezier path coordinates.",
        "Modify or delete individual node command rows in the step index builder.",
        "Copy the newly built coordinates parameter to paste into any vector markup."
      ]}
      faq={[
        { question: "What is an SVG path and how is it defined?", answer: "An SVG path is a powerful, flexible vector element defined using the `<path>` tag. Its shape is controlled by a 'd' (draw) attribute string that instructs the vector renderer where to move, draw line strokes, or calculate curves on a grid." },
        { question: "What do the different letters in an SVG path string represent?", answer: "The letters represent command directions: M = Move To (reposition brush), L = Line To (draw straight line), C = Cubic Bezier curve, Q = Quadratic Bezier curve, A = Elliptical Arc, and Z = Close Path (draw line back to origin)." },
        { question: "What is the difference between uppercase and lowercase path commands?", answer: "Uppercase letters (M, L, C, Z) execute commands using absolute coordinates relative to the 0,0 grid origin. Lowercase letters (m, l, c, z) execute commands using relative offset coordinates from the current cursor point." },
        { question: "How does a Cubic Bezier (C) curve differ from a Quadratic Bezier (Q)?", answer: "A Cubic Bezier (C) curve requires two independent control points to guide the slope from start to finish, allowing S-curves. A Quadratic Bezier (Q) curve uses a single shared control point, creating simpler symmetrical arches." },
        { question: "How does the SVG path editor help developers and designers?", answer: "It decomposes complex path coordinate codes into highly readable, isolated step-by-step commands, rendering them on a visual plane with interactive scaling for precise geometric alignment." },
        { question: "Is this SVG path coordinate builder fully secure?", answer: "Yes, all coordinate calculations, syntax checks, path listings, and vector preview renderings occur purely locally in your web browser, ensuring complete privacy." },
        { question: "Why does my custom path element fail to render correctly in other editors?", answer: "Ensure that coordinate digits are separated by spaces or commas, brackets or custom tags are not nested inside the 'd' attribute, and your path starts with an 'M' or 'm' command." },
        { question: "How can I easily convert an SVG path into React code?", answer: "Paste your generated path string into a standard JSX element: `<path d='...' fill='#ff0000' />`. This is compatible with all React web frameworks." },
        { question: "What is the Close Path (Z) command used for?", answer: "The 'Z' (or 'z') command draws a direct straight stroke from the brush's current location back to the start of the current path loop, securely sealing the shape for seamless fill coloring." },
        { question: "Does this path editor support absolute coordinate grids?", answer: "Yes. It displays coordinates on an absolute 100x100 grid plane, making it ideal for checking layout metrics, icon parameters, and responsive scales." }
      ]}
    >
      <div className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Visual Display and Editor Box */}
          <div className="space-y-4">
            <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-xs space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-slate-100 text-rose-600">
                <PenTool className="w-5 h-5" />
                <span className="font-bold text-sm uppercase tracking-wider">Interactive Path Preview</span>
              </div>

              {/* 100x100 Grid Visual Stage */}
              <div className="flex justify-center items-center bg-slate-900 rounded-xl p-4 min-h-[250px] relative">
                {/* SVG Coordinate Grid */}
                <svg
                  viewBox="0 0 100 100"
                  className="w-full max-w-[220px] aspect-square border border-slate-700 bg-slate-950 relative"
                >
                  {/* Grid Lines */}
                  {[10, 20, 30, 40, 50, 60, 70, 80, 90].map(grid => (
                    <React.Fragment key={grid}>
                      <line x1={grid} y1="0" x2={grid} y2="100" stroke="#334155" strokeWidth="0.15" strokeDasharray="1 1" />
                      <line x1="0" y1={grid} x2="100" y2={grid} stroke="#334155" strokeWidth="0.15" strokeDasharray="1 1" />
                    </React.Fragment>
                  ))}
                  {/* Axes */}
                  <line x1="50" y1="0" x2="50" y2="100" stroke="#475569" strokeWidth="0.3" />
                  <line x1="0" y1="50" x2="100" y2="50" stroke="#475569" strokeWidth="0.3" />

                  {/* Render the actual path */}
                  {pathString && (
                    <path
                      d={pathString}
                      fill="rgba(244, 63, 94, 0.2)"
                      stroke="#f43f5e"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  )}

                  {/* Control nodes indicator */}
                  {commands.map((cmd, idx) => {
                    if (cmd.type === "Z" || cmd.params.length < 2) return null;
                    const px = cmd.params[cmd.params.length - 2];
                    const py = cmd.params[cmd.params.length - 1];
                    return (
                      <circle
                        key={idx}
                        cx={px}
                        cy={py}
                        r="2.5"
                        fill="#ffffff"
                        stroke="#f43f5e"
                        strokeWidth="1.0"
                      />
                    );
                  })}
                </svg>
              </div>

              {/* Path Code Text Input */}
              <div className="space-y-1.5">
                <div className="flex justify-between items-center">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Path 'd' Coordinates String</Label>
                  <button
                    onClick={handleClear}
                    className="text-[10px] text-slate-400 hover:text-red-500"
                  >
                    Clear Path
                  </button>
                </div>
                <Input
                  value={pathString}
                  onChange={(e) => setPathString(e.target.value)}
                  className="font-mono text-xs text-rose-700 font-bold tracking-wide"
                  placeholder="e.g. M 10 10 L 90 90 Z"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleCopy}
                  disabled={!pathString}
                  className="flex-1 bg-slate-900 hover:bg-slate-800 text-white gap-2"
                >
                  <Copy className="w-4 h-4" />
                  {copied ? "Copied Coordinates!" : "Copy Path Coordinates"}
                </Button>
              </div>
            </div>
          </div>

          {/* Path Steps & Generator Tool Panel */}
          <div className="space-y-4">
            {/* Quick builder form */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-xs space-y-4">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">Quick Path Builder Step</span>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[11px] font-bold text-slate-600">Command Type</Label>
                  <select
                    value={newType}
                    onChange={(e) => setNewType(e.target.value as PathCommand["type"])}
                    className="w-full text-xs rounded-lg border border-slate-200 p-2"
                  >
                    <option value="M">M - Move To</option>
                    <option value="L">L - Line To</option>
                    <option value="C">C - Cubic Bezier</option>
                    <option value="Q">Q - Quadratic Bezier</option>
                    <option value="Z">Z - Close Path</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <Label className="text-[11px] font-bold text-slate-600">Point Coordinates (X, Y)</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      value={x}
                      onChange={(e) => setX(e.target.value)}
                      className="h-8 text-xs p-1.5 text-center font-mono"
                      placeholder="X"
                    />
                    <Input
                      type="number"
                      value={y}
                      onChange={(e) => setY(e.target.value)}
                      className="h-8 text-xs p-1.5 text-center font-mono"
                      placeholder="Y"
                    />
                  </div>
                </div>
              </div>

              {/* Extra Bezier parameters */}
              {(newType === "C" || newType === "Q") && (
                <div className="grid grid-cols-2 gap-3 bg-slate-50 p-3 rounded-lg border border-slate-100">
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold text-slate-500">Control Pt 1 (cx1, cy1)</Label>
                    <div className="flex gap-1.5">
                      <Input
                        type="number"
                        value={cx1}
                        onChange={(e) => setCx1(e.target.value)}
                        className="h-7 text-xs text-center font-mono"
                        placeholder="cx1"
                      />
                      <Input
                        type="number"
                        value={cy1}
                        onChange={(e) => setCy1(e.target.value)}
                        className="h-7 text-xs text-center font-mono"
                        placeholder="cy1"
                      />
                    </div>
                  </div>

                  {newType === "C" && (
                    <div className="space-y-1">
                      <Label className="text-[10px] font-bold text-slate-500">Control Pt 2 (cx2, cy2)</Label>
                      <div className="flex gap-1.5">
                        <Input
                          type="number"
                          value={cx2}
                          onChange={(e) => setCx2(e.target.value)}
                          className="h-7 text-xs text-center font-mono"
                          placeholder="cx2"
                        />
                        <Input
                          type="number"
                          value={cy2}
                          onChange={(e) => setCy2(e.target.value)}
                          className="h-7 text-xs text-center font-mono"
                          placeholder="cy2"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              <Button
                onClick={handleAddCommand}
                className="w-full bg-rose-600 hover:bg-rose-700 text-white gap-1.5 text-xs h-9"
              >
                <Plus className="w-4 h-4" /> Append Command To Path
              </Button>
            </div>

            {/* List of current path commands */}
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 shadow-xs space-y-3 max-h-[300px] overflow-auto">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">Command Steps Index</span>
              
              {commands.length > 0 ? (
                <div className="space-y-2">
                  {commands.map((cmd, idx) => (
                    <div
                      key={idx}
                      className="bg-white border border-slate-100 rounded-lg p-2.5 flex items-center justify-between text-xs"
                    >
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-rose-50 text-rose-600 font-bold flex items-center justify-center text-[10px]">
                          {idx + 1}
                        </span>
                        <code className="font-mono bg-slate-50 px-1.5 py-0.5 rounded text-rose-700 font-bold">
                          {cmd.type}
                        </code>
                        <span className="text-slate-500 font-mono">
                          {cmd.params.length > 0 ? `[${cmd.params.join(", ")}]` : "N/A"}
                        </span>
                      </div>

                      <button
                        onClick={() => handleDeleteCommand(idx)}
                        className="text-[10px] text-slate-400 hover:text-red-500"
                        title="Delete Step"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400 italic">No commands present. Add steps to construct a path outline.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}
